import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Zap, LogIn, UserPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  useEffect(() => {
    // Check if admin exists
    const checkAdmin = async () => {
      try {
        const response = await axios.get(`${API}/admin/check`);
        setIsRegister(!response.data.exists);
      } catch (error) {
        console.error("Error checking admin:", error);
      } finally {
        setCheckingAdmin(false);
      }
    };

    // Check if already logged in
    const token = localStorage.getItem("admin_token");
    if (token) {
      navigate("/admin/dashboard");
    } else {
      checkAdmin();
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      if (isRegister) {
        await axios.post(`${API}/admin/register`, formData);
        toast.success("Admin registered! Please login.");
        setIsRegister(false);
        setFormData({ username: "", password: "" });
      } else {
        const response = await axios.post(`${API}/admin/login`, formData);
        localStorage.setItem("admin_token", response.data.token);
        localStorage.setItem("admin_username", response.data.username);
        toast.success("Welcome back!");
        navigate("/admin/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  if (checkingAdmin) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center">
            <Zap className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-heading text-2xl tracking-wide">TEAMPOWER</h1>
            <p className="text-zinc-500 text-sm">Admin Panel</p>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8">
          <h2 className="font-heading text-xl mb-6 text-center">
            {isRegister ? "Create Admin Account" : "Admin Login"}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
            <div>
              <Label htmlFor="username" className="text-zinc-400">Username</Label>
              <Input
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="mt-2 bg-zinc-800 border-zinc-700 text-white"
                placeholder="Enter username"
                data-testid="username-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-zinc-400">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-2 bg-zinc-800 border-zinc-700 text-white"
                placeholder="Enter password"
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold h-12 glow-orange glow-orange-hover"
              data-testid="submit-button"
            >
              {loading ? (
                "Please wait..."
              ) : isRegister ? (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create Account
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Login
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-zinc-400 hover:text-white"
          >
            Back to Home
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
